import React, { useEffect } from 'react';
import { withStyles, CircularProgress, Dialog } from '@material-ui/core';
import * as Yup from "yup";
import { hasPermission } from '../../../sc-utilities';
import CustomerForm from '../CustomerForm'
import { CancelToken } from 'axios';
import AppLogoLoading from '../../../sc-logo';
import countries from 'i18n-iso-countries';
import config from '../../../../config/config';
import { states, getStatesList, getCountryList } from '../../../sc-utilities/CountriesAndUSstatesList';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        margin: theme.spacing(1),
        padding: 3 * 8,
    },
    paper: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    logo: {
        width: '250px',
        margin: '10px',
    },
});

export const allowedLicensingValues = ["default_off", "default_on", "always_off", "always_on"];

const CustomerEdit = props => {
    const { classes, setLoading, customer, loading, onUpdateCustomer, onLoadCustomer, activeCustomer, customerID, translations, permissions, userLanguage, accountManagement } = props;
    try {
        let lang = userLanguage;
        if (userLanguage.includes("zh-")) {
            lang = "zh";
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${lang}.json`));
    } catch (error) {
        console.log(error)
    }

    const cancelToken = CancelToken?.source();
    const cancelTokenUpdate = CancelToken?.source();
    useEffect(() => {
        onLoadCustomer({ CustomerID: customerID, ActiveCustomerID: activeCustomer }, { cancelToken: cancelToken });
        return () => {
            cancelToken.cancel('Operation canceled by the user.');
            cancelTokenUpdate.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerID, activeCustomer, onLoadCustomer]);

    const fetchCustomer = async () => {
        setLoading(true);
        await onLoadCustomer({ ActiveCustomerID: activeCustomer, CustomerID: customerID }, { cancelToken: cancelToken })
        setLoading(false);
    };

    const countryList = getCountryList(countries, userLanguage);
    const stateList = getStatesList(states);

    let customerAddress = {};
    try {
        if (customer && customer.Address) {
            if (typeof customer.Address === 'string') {
                customerAddress = JSON.parse(customer.Address);
            } else {
                customerAddress = customer.Address;
            }
        }
        if (customerAddress.Country) {
            const findCountryObj = countryList.find(element => element.key === customerAddress.Country);
            customerAddress.Country = findCountryObj || customerAddress.Country;
        }

        if (customerAddress.State) {
            const findCountryObj = stateList.find(element => element.key === customerAddress.State);
            customerAddress.State = findCountryObj || customerAddress.State;
        }

    } catch (error) {

    }

    let initialValuesCustomer = {
        Name: customer?.Name || "",
        Namespace: customer?.Namespace || "",
        AddressLine1: customerAddress?.AddressLine1 || "",
        AddressLine2: customerAddress?.AddressLine2 || "",
        State: customerAddress?.State || '',
        City: customerAddress?.City || "",
        Country: customerAddress?.Country || "",
        Status: customer?.Status === 0 || customer?.status === 'inactive' ? 'inactive' : 'active',
        CreateDefaultData: customer?.CreateDefaultData || "yes",
        AutoLogout: customer?.AutoLogout || 0,
        Timezone: customer?.Timezone && customer?.Timezone !== '' ? customer?.Timezone : config?.timezone,
        PasswordComplexity: customer?.PasswordComplexity || "securaze_basic",
        IndividualLicensingSetting: customer?.IndividualLicensingSetting || "default_off",
        GroupLicensingSetting: customer?.GroupLicensingSetting || "default_off",
        UniqueInventoryNumberEnabled: customer?.UniqueInventoryNumberEnabled || false,
    };

    let formSettingsCustomer = {
        Validation: {
            Name: Yup.string().required("Required"),
            Namespace: Yup.string()
                .trim()
                .matches(/^[a-z0-9_-]+$/, 'Is not in correct format'),
            AddressLine1: Yup.string().required("Required"),
            AddressLine2: Yup.string().notRequired("Required"),
            City: Yup.string().required("Required"),
            State: Yup.string().when("Country", {
                is: "United States of America",
                then: Yup.string().required()
            }),
            Country: Yup.string().required("Required"),
            Status: Yup.string().required("Required"),
            AutoLogout: Yup.number().required("Required"),
            PasswordComplexity: Yup.string().required("Required"),
            IndividualLicensingSetting: Yup.string().oneOf(allowedLicensingValues).required("Required"),
            GroupLicensingSetting: Yup.string().oneOf(allowedLicensingValues).required("Required"),
            UniqueInventoryNumberEnabled: Yup.boolean().notRequired(),
        },
        ButtonSubmit: {
            Title: hasPermission(permissions, 'customer_save') ? translations["save"] || "Save" : '',
            Color: 'primary'
        }
    };

    if (!hasPermission(permissions, 'customer_save')) {
        delete formSettingsCustomer['ButtonSubmit']
    };

    const handleEdit = async data => {
        data['CustomerID'] = customer.CustomerID;
        const Address = {
            AddressLine1: data?.AddressLine1,
            AddressLine2: data?.AddressLine2,
            City: data?.City,
            State: data?.State?.key || data?.State || null,
            Country: data?.Country?.key || data?.Country || null
        };
        data['Address'] = JSON.stringify(Address);
        setLoading(false);
        let result = await onUpdateCustomer({ ...data, Status: data['Status'] === 'active' ? 1 : 0, ActiveCustomerID: activeCustomer }, { cancelToken: cancelTokenUpdate });
        if (result) {
            await fetchCustomer();
            setLoading(false);
            if (accountManagement) {
                window.localStorage.setItem("CustomerTimezone", data.Timezone ?? '');
            }
        }
    };

    return (
        <>
            <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={loading}
                PaperProps={{
                    classes: {
                        root: classes.paper
                    }
                }}
            >
                <AppLogoLoading />
            </Dialog>
            {customer ?
                <CustomerForm
                    countryList={countryList}
                    usStatesList={stateList}
                    formSettings={formSettingsCustomer}
                    setLoading={setLoading}
                    initialValues={initialValuesCustomer}
                    handleSubmit={handleEdit}
                    edit={true}
                    permissions={permissions}
                    translations={translations}
                ></CustomerForm> :
                <CircularProgress size={24} />
            }
        </>
    )
};

export default withStyles(styles, { withTheme: true })(CustomerEdit);

